import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { motion } from "framer-motion";
import {
  Briefcase,
  Calendar,
  User,
  MessageSquare,
  ArrowLeft,
  Coins,
} from "lucide-react";
import { UserContext } from "../context/UserContext";
import AuthModal from "../components/modal/AuthModal";
import { toast } from "react-toastify";

const FreelanceProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [applied, setApplied] = useState(false);
  const { user } = useContext(UserContext);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const docRef = doc(db, "freelanceProjects", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setProject({ id: snap.id, ...snap.data() });
        } else {
          console.error("No project found");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  useEffect(() => {
    const checkIfApplied = async () => {
      if (!user) return;
      try {
        const q = query(
          collection(db, "projectApplications"),
          where("projectId", "==", id),
          where("userId", "==", user.id)
        );
        const snap = await getDocs(q);
        if (!snap.empty) setApplied(true);
      } catch (err) {
        console.error("Error checking application:", err);
      }
    };

    checkIfApplied();
  }, [user, id]);

  const handleDiscuss = () => {
    if (!user) return setShowAuthModal(true);
    navigate(`/chat/${id}`, { state: { project } });
  };

  const handleApply = async () => {
    if (!user) return setShowAuthModal(true);

    if (applied) {
      toast.info("You’ve already shown interest in this project.");
      return;
    }

    try {
      await addDoc(collection(db, "projectApplications"), {
        projectId: id,
        userId: user.id,
        userName: user.name || "Anonymous",
        userImage: user.profilePic || "",
        projectTitle: project.title,
        projectOwnerId: project.posterId || null,
        createdAt: serverTimestamp(),
      });

      toast.success("You have successfully shown interest in this project!");
      setApplied(true);
    } catch (err) {
      console.error("Error applying:", err);
      toast.error("Failed to apply. Please try again.");
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <Loading>Loading project details...</Loading>
      </Wrapper>
    );
  }

  if (!project) {
    return (
      <Wrapper>
        <ErrorText>❌ Project not found</ErrorText>
        <BackButton onClick={() => navigate(-1)}>← Go Back</BackButton>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </BackButton>

        <Card>
          <CardHeader>
            <HeaderLeft>
              <Briefcase size={24} color="#c59fff" />
              <h2>{project.title}</h2>
            </HeaderLeft>
            {project.category && <CategoryTag>{project.category}</CategoryTag>}
          </CardHeader>

          <Description>{project.description}</Description>

          {project.skills && (
            <Skills>
              {project.skills.map((s, i) => (
                <Skill key={i}>{s}</Skill>
              ))}
            </Skills>
          )}

          <MetaInfo>
            <InfoItem>
              <Calendar size={16} />{" "}
              {project.createdAt?.seconds
                ? new Date(
                    project.createdAt.seconds * 1000
                  ).toLocaleDateString()
                : "Recently"}
            </InfoItem>
            <InfoItem>
              <Coins size={16} />{" "}
              {project.budget ? `₹${project.budget}` : "Budget not set"}
            </InfoItem>
          </MetaInfo>

          <PostedBy>
            {project.userImage ? (
              <Avatar src={project.userImage} />
            ) : (
              <User size={20} color="#c59fff" />
            )}
            <span>Posted by {project.postedBy || "Anonymous"}</span>
          </PostedBy>

          <ButtonGroup>
            <PrimaryButton onClick={handleApply} disabled={applied}>
              {applied ? "Interest Shown ✅" : "Apply / Show Interest"}
            </PrimaryButton>
            <SecondaryButton onClick={handleDiscuss}>
              <MessageSquare size={16} /> Discuss
            </SecondaryButton>
          </ButtonGroup>
        </Card>
      </motion.div>
    </Wrapper>
  );
};

export default FreelanceProjectDetail;

/* --- existing styles remain unchanged --- */

/* ---------------- Styled Components ---------------- */

const Wrapper = styled.section`
  width: 100%;
  min-height: 100vh;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 80px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Card = styled.div`
  width: 100%;
  max-width: 750px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(8px);
  box-shadow: 0 0 20px rgba(155, 92, 255, 0.2);

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;

  h2 {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CategoryTag = styled.span`
  background: rgba(155, 92, 255, 0.2);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  color: #c59fff;
  font-size: 0.85rem;
  font-weight: 500;
`;

const Description = styled.p`
  color: #ccc;
  font-size: 1rem;
  line-height: 1.6;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const Skill = styled.span`
  background: rgba(155, 92, 255, 0.2);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.85rem;
  color: #c59fff;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #aaa;
  font-size: 0.9rem;
`;

const PostedBy = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 30px;
  color: #dcdcdc;

  span {
    font-size: 0.95rem;
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(155, 92, 255, 0.4);
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.4);
  color: #fff;

  &:hover {
    background: rgba(155, 92, 255, 0.15);
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: #c59fff;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin-bottom: 24px;

  &:hover {
    text-decoration: underline;
  }
`;

const Loading = styled.p`
  color: #c59fff;
  font-size: 1rem;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #ff7a7a;
  font-size: 1rem;
  text-align: center;
`;
